import { throwError } from "rxjs";
import { ajax, AjaxResponse } from "rxjs/ajax";
import { catchError } from "rxjs/operators";
import {
  ApiMessageResponse,
  AppointmentRow,
  AvailabilityResponse,
  AppSettingsResponse,
  BookResponse,
  DashboardTodayResponse,
  DisableRuleRow,
  LoginResponse,
  PatientSearchResponse,
  PhoneHistoryResponse,
} from "./api.types";

export const baseURL =
  window.location.hostname === "localhost"
    ? "http://localhost/nt/server"
    : "https://naveedtazeen.com/Server";

const requestHeader = () => ({});

const handleError = (operation: string) => (err: AjaxResponse<object>) => {
  const errMsg = `Error in ${operation}(): ${err.status || "network error"}`;
  console.error(errMsg, err);

  if (err.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
  }
  return throwError(() => new Error(errMsg));
};

export const postLogin = (data: { password: string }) => {
  const formData = new FormData();
  formData.append("password", data.password);

  return ajax<LoginResponse>({
    headers: requestHeader(),
    method: "POST",
    url: `${baseURL}/login.php`,
    body: formData,
  }).pipe(catchError(handleError("postLogin")));
};

export const disableBookingDate = (date: string) => {
  return ajax<ApiMessageResponse>({
    headers: requestHeader(),
    method: "POST",
    url: `${baseURL}/disable-book.php?date=${date}`,
  }).pipe(catchError(handleError("disableBookingDate")));
};

export const getAllAppointments = (date: string | Date) => {
  return ajax<AppointmentRow[]>({
    method: "GET",
    url: `${baseURL}/getall.php?date=${date}`,
  }).pipe(catchError(handleError("getAllAppointments")));
};

export const getAppointmentsByPhone = (phone: string) => {
  return ajax<PhoneHistoryResponse>({
    method: "GET",
    url: `${baseURL}/getapbyphone.php?phone=${encodeURIComponent(phone)}`,
  }).pipe(catchError(handleError("getAppointmentsByPhone")));
};

export const getAvailability = (date: string, bookingType = "normal") => {
  return ajax<AvailabilityResponse>({
    method: "GET",
    url: `${baseURL}/checkdate.php?date=${date}&type=${bookingType}`,
  }).pipe(catchError(handleError("getAvailability")));
};

export const bookAppointment = (data: FormData) => {
  return ajax<BookResponse>({
    method: "POST",
    url: `${baseURL}/book.php`,
    body: data,
  }).pipe(catchError(handleError("bookAppointment")));
};

export const getLogs = () => {
  return ajax<unknown[]>({
    method: "GET",
    url: `${baseURL}/getlogs.php`,
  }).pipe(catchError(handleError("getLogs")));
};

export const getDisableDays = () => {
  return ajax<DisableRuleRow[]>({
    method: "GET",
    url: `${baseURL}/fetch_rules.php`,
  }).pipe(catchError(handleError("getDisableDays")));
};

export const deleteAppointment = (id: number) => {
  return ajax<ApiMessageResponse>({
    method: "DELETE",
    url: `${baseURL}/delete.php?id=${id}`,
  }).pipe(catchError(handleError("deleteAppointment")));
};

export const deleteRule = (id: number) => {
  return ajax<ApiMessageResponse>({
    method: "DELETE",
    url: `${baseURL}/deleterule.php?id=${id}`,
  }).pipe(catchError(handleError("deleteRule")));
};

export interface UpdateAppointmentPayload {
  name: string;
  phone: string;
  guardianName: string;
  date?: string;
  booking_type?: string;
}

export const updateAppointment = (id: number, data: UpdateAppointmentPayload) => {
  return ajax<ApiMessageResponse>({
    method: "PUT",
    url: `${baseURL}/update.php?id=${id}`,
    body: { data },
  }).pipe(catchError(handleError("updateAppointment")));
};

export const getReports = (from: string, to: string, days = 14) => {
  return ajax<unknown>({
    method: "GET",
    url: `${baseURL}/reports.php?from=${from}&to=${to}&days=${days}`,
  }).pipe(catchError(handleError("getReports")));
};

export const getDashboardToday = () => {
  const today = new Date().toISOString().split("T")[0];
  return ajax<DashboardTodayResponse>({
    method: "GET",
    url: `${baseURL}/reports.php?type=today&from=${today}&to=${today}`,
  }).pipe(catchError(handleError("getDashboardToday")));
};

export const getAllPatients = () => {
  return ajax<PatientSearchResponse>({
    method: "GET",
    url: `${baseURL}/search-patients.php`,
  }).pipe(catchError(handleError("getAllPatients")));
};

/** @deprecated use getAllPatients — search is client-side */
export const searchPatients = getAllPatients;

export const getGeneralRules = () => {
  return ajax<{ status: string; rules: unknown[] }>({
    method: "GET",
    url: `${baseURL}/get-general-rules.php`,
  }).pipe(catchError(handleError("getGeneralRules")));
};

export const updateGeneralRule = (
  id: number,
  data: { enabled?: boolean; urgent_limit?: number; normal_limit?: number }
) => {
  return ajax<{ status: string; rule?: unknown; message?: string }>({
    method: "PUT",
    url: `${baseURL}/update-general-rule.php?id=${id}`,
    headers: { "Content-Type": "application/json" },
    body: data,
  }).pipe(catchError(handleError("updateGeneralRule")));
};

export const getSettings = () => {
  return ajax<AppSettingsResponse>({
    method: "GET",
    url: `${baseURL}/get-settings.php`,
  }).pipe(catchError(handleError("getSettings")));
};

export const updateSettings = (data: { enable_urgent_bookings: boolean }) => {
  return ajax<AppSettingsResponse>({
    method: "POST",
    url: `${baseURL}/update-settings.php`,
    headers: { "Content-Type": "application/json" },
    body: data,
  }).pipe(catchError(handleError("updateSettings")));
};

/** @deprecated use postLogin */
export const PostData = postLogin;
/** @deprecated use disableBookingDate */
export const DisableDays = disableBookingDate;
/** @deprecated use getAvailability */
export const GetAvailablity = getAvailability;
/** @deprecated use getAppointmentsByPhone */
export const GetBookedAppointments = getAppointmentsByPhone;
/** @deprecated use updateAppointment */
export const update = updateAppointment;
